/**
 * PercussionLogic.js
 * Handles mapping of abstract percussion roles to specific Orchestral Percussion notes.
 * Also generates procedural Timpani rolls.
 */

const PERCUSSION_CONFIG = {
    mapping: {
        BASS_DRUM: 36, // C1
        SNARE: 38,     // D1
        SNARE_ROLL: 39,
        PIATTI: 49,    // C#2
        TAM_TAM: 53,   // F2
        CLOSED_HAT: 42,
        TIMPANI_ROLL_KEY: 1,
        TIMPANI_HIT_KEY: 0
    },
    flam: {
        chanceOnAccent: 0.2,
        graceNoteOffset: 0.05
    },
    velocities: {
        accent: { base: 0.9, var: 0.1 },
        ghost: { base: 0.6, var: 0.1 },
        roll: { start: 0.1, end: 0.8 },
        crash: 1.0,
        bd: { base: 0.9, var: 0.1 }
    }
};

/**
 * Maps abstract roles to specific MIDI notes for the Orchestral Percussion patch.
 * @param {string} role - 'perc_low', 'perc_mid', 'perc_high'
 * @param {boolean} isClimax - Is this a loud section?
 */
export const getPercussionNote = (role, isClimax) => {
    if (role === 'perc_low') return PERCUSSION_CONFIG.mapping.BASS_DRUM;
    if (role === 'perc_mid') return PERCUSSION_CONFIG.mapping.SNARE;
    if (role === 'perc_high') return isClimax ? PERCUSSION_CONFIG.mapping.PIATTI : PERCUSSION_CONFIG.mapping.CLOSED_HAT;
    return PERCUSSION_CONFIG.mapping.BASS_DRUM;
};

/**
 * Maps a Pitch Class to the Timpani's playable range (C2-E3 / 36-52).
 * Prefers the Root or 5th.
 * @param {number} rootPC - Pitch Class (0-11).
 * @returns {number} MIDI Note.
 */
export const getTimpaniPitch = (rootPC) => {
    // Basic Map: C2 (36) + PC
    let note = 36 + (rootPC % 12);
    // If note > 52 (E3), drop octave? (Not possible since 36+11=47)
    // Range is 36 to 52.
    // 36 (C) to 47 (B) is always safe.
    return note;
};

/**
 * Generates a Timpani Roll Swell.
 * @param {number} startTime - Start time.
 * @param {number} duration - Duration (usually 1 bar).
 * @param {number} rootPitch - Root Pitch Class (for tuning).
 */
export const generateTimpaniRoll = (startTime, duration, rootPitch = 0) => {
    // Tuning
    const note = getTimpaniPitch(rootPitch);

    const events = [];
    const step = 0.125; // 32nd notes (approx)
    const count = Math.floor(duration / step);

    for (let i = 0; i < count; i++) {
        const progress = i / count;
        // Crescendo Curve (Exponential)
        const velocity = 0.2 + (Math.pow(progress, 2) * 0.7);

        events.push({
            s: startTime + (i * step),
            d: note,
            notes: [note],
            len: step, // Raw grid duration
            vel: velocity,
            role: 'perc_low',
            articulation: 'roll'
        });
    }

    // Add final accent
    events.push({
        s: startTime + duration,
        d: note,
        notes: [note],
        len: 0.25, // Standard quarter/16th hit duration
        vel: 1.0,
        role: 'perc_low',
        articulation: 'hit'
    });

    return events;
};

/**
 * Generates a Cymbal Crash (Piatti).
 */
export const generateCymbalCrash = (startTime) => {
    return {
        s: startTime,
        d: PERCUSSION_CONFIG.mapping.PIATTI,
        len: 0.25, // Changed from 2.0 to standard hit duration
        vel: PERCUSSION_CONFIG.velocities.crash,
        articulation: 'hit'
    };
};

/**
 * Action Groove Library
 * Defines layers of cinematic percussion patterns.
 */
const ACTION_GROOVES = {
    standard_march: {
        bd: [0, 1, 2, 3],
        snare: [0.5, 1.5, 2.5, 3.5],
        snare_accents: [1, 3],
        timeSig: [4, 4]
    },
    tresillo: {
        bd: [0, 1.5, 3],
        snare: [0, 0.75, 1.5, 2.25, 3, 3.5],
        snare_accents: [0, 1.5, 3],
        timeSig: [4, 4]
    },
    chase: {
        bd: [0, 0.75, 2, 2.75],
        snare: [0.25, 0.5, 1.0, 1.25, 1.5, 2.25, 2.5, 3.0, 3.25, 3.5],
        snare_accents: [1, 3],
        timeSig: [4, 4],
        forced: true // Priority override
    },
    war_drums: {
        bd: [0, 0.5, 1.5, 2, 3.5],
        snare: [1, 3],
        snare_accents: [1, 3],
        timeSig: [4, 4]
    },
    taiko_heavy: {
        bd: [0, 1.5, 2, 3.5],
        snare: [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5],
        snare_accents: [0, 2],
        timeSig: [4, 4]
    },
    waltz_basic: {
        bd: [0],
        snare: [1, 2],
        snare_accents: [1, 2],
        timeSig: [3, 4]
    },
    jazz_brushes: {
        bd: [0, 2.5],
        snare: [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5],
        snare_accents: [1, 3],
        timeSig: [4, 4]
    },
    sleigh_ride: {
        bd: [0, 1, 2, 3],
        snare: [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5],
        snare_accents: [0, 1, 2, 3],
        timeSig: [4, 4]
    },
    western_gallop: {
        bd: [0, 2],
        snare: [0.25, 0.5, 0.75, 1.25, 1.5, 1.75, 2.25, 2.5, 2.75, 3.25, 3.5, 3.75],
        snare_accents: [0.5, 1.5, 2.5, 3.5],
        timeSig: [4, 4]
    },
    tabor_rhythm: {
        bd: [0, 1.5],
        snare: [0, 1.5, 2, 2.5, 3],
        snare_accents: [0, 1.5],
        timeSig: [4, 4]
    }
};

/**
 * Generates a full orchestral battery (Action/Epic style).
 * @param {number} barStart - Start time of the bar.
 * @param {string} intensity - 'low', 'med', 'high'
 * @param {Object} random - Random generator
 * @param {Object} context - { profile, timeSignature }
 */
export const generateActionBattery = (barStart, intensity = 'med', random, context = {}) => {
    const events = [];
    const { profile, timeSignature = [4, 4] } = context;

    // Filter grooves by Time Signature if provided
    let availableGrooves = Object.keys(ACTION_GROOVES).filter(key => {
        const groove = ACTION_GROOVES[key];
        return !groove.timeSig || (groove.timeSig[0] === timeSignature[0] && groove.timeSig[1] === timeSignature[1]);
    });

    if (availableGrooves.length === 0) {
        availableGrooves = ['standard_march'];
    }

    // Select Groove Pattern
    let grooveKey = 'standard_march';

    // ENHANCEMENT: Enforce 'chase' for Action/Chase unless overridden
    if (!profile && availableGrooves.includes('chase') && context.profile === 'action') {
        grooveKey = 'chase';
    } else if (profile && ACTION_GROOVES[profile] && availableGrooves.includes(profile)) {
        grooveKey = profile;
    } else {
        grooveKey = random ? random.pick(availableGrooves) : availableGrooves[Math.floor(Math.random() * availableGrooves.length)];
    }

    const pattern = ACTION_GROOVES[grooveKey];

    // 1. Bass Drum Layer
    let bdHits = pattern.bd;
    if (intensity === 'low') {
        bdHits = [0]; // Just downbeat
    }

    // SOFT LOCK LOGIC (Phase 1)
    // Merge Groove Hits with Bass Accents
    let bassAccents = new Set();
    if (context.bassRhythm && context.bassRhythm.length > 0) {
        // Quantize bass hits to 16th grid for alignment check
        context.bassRhythm.forEach(t => bassAccents.add(Math.round(t * 100) / 100));

        const grooveHits = new Set(bdHits.map(t => Math.round(t * 100) / 100));
        const combined = new Set([...grooveHits]);
        bassAccents.forEach(t => combined.add(t));

        bdHits = Array.from(combined).sort((a,b) => a - b);
    }

    bdHits.forEach(t => {
        let vel = PERCUSSION_CONFIG.velocities.bd.base;

        // Velocity Logic: Boost Locked hits, Ghost fillers
        const qTime = Math.round(t * 100) / 100;
        const isBassLock = bassAccents.has(qTime);
        const isGroove = pattern.bd.includes(t); // Original pattern check (approx)

        if (isBassLock) {
            vel = 1.0; // Strong lock
        } else if (isGroove) {
            // Filler kick
            vel = 0.8;
        } else {
            // Should not happen if we only merged sets
            vel = 0.7;
        }

        // Add random variance
        vel += ((random ? random.next() : Math.random()) * PERCUSSION_CONFIG.velocities.bd.var);

        events.push({
            s: barStart + t,
            d: PERCUSSION_CONFIG.mapping.BASS_DRUM,
            vel: Math.min(1.0, vel),
            role: 'perc_low',
            len: 0.25, // Replaced magic 0.1 with 16th
            articulation: 'hit'
        });
    });

    // 2. Snare Layer
    if (intensity !== 'low') {
        const snareHits = pattern.snare;
        const snareAccents = new Set(pattern.snare_accents);

        snareHits.forEach(t => {
            // Skip some non-accent hits in Medium intensity
            if (intensity === 'med' && !snareAccents.has(t) && (random ? random.next() : Math.random()) > 0.6) return;

            const isAccent = snareAccents.has(t);
            const baseVel = isAccent ? PERCUSSION_CONFIG.velocities.accent.base : PERCUSSION_CONFIG.velocities.ghost.base;
            const velVar = isAccent ? PERCUSSION_CONFIG.velocities.accent.var : PERCUSSION_CONFIG.velocities.ghost.var;

            events.push({
                s: barStart + t,
                d: PERCUSSION_CONFIG.mapping.SNARE,
                vel: baseVel + ((random ? random.next() : Math.random()) * velVar),
                role: 'perc_mid',
                len: 0.25, // Replaced magic 0.1 with 16th
                articulation: isAccent ? 'hit' : 'ghost'
            });
        });
    }

    // 3. High Percussion (Piatti / Metals)
    if (intensity === 'high' && (random ? random.next() : Math.random()) > 0.5) {
        // Occasional crashes on accents
        pattern.bd.forEach(t => {
            if (t % 2 === 0) { // On beat 1 or 3
                 events.push({
                    s: barStart + t,
                    d: PERCUSSION_CONFIG.mapping.PIATTI,
                    vel: PERCUSSION_CONFIG.velocities.crash,
                    role: 'perc_high',
                    len: 0.25, // Replaced 1.0 with standard hit
                    articulation: 'hit'
                });
            }
        });
    }

    return events;
};

/**
 * Task 2: "The Percussion Gate" (Contextual Speed Limit)
 * Filters percussion events based on Global Tension to prevent "Machine Gunning".
 * @param {Array} events - List of percussion events.
 * @param {number} tension - Global Tension (0.0 - 1.0).
 */
export const filterPercussionByTension = (events, tension = 0.5) => {
    if (!events || events.length === 0) return [];

    return events.filter(ev => {
        // Allow Fills/Rolls/Crashes explicitly
        if (ev.articulation === 'roll' || ev.articulation === 'fill' || ev.role === 'perc_high') return true;

        const beatPos = ev.s % 4.0;
        const isDownbeat = Math.abs(beatPos) < 0.1;
        const isBackbeat = Math.abs(beatPos - 2.0) < 0.1; // Beat 3 (Snare)
        const isQuarter = Math.abs(ev.s % 1.0) < 0.1;
        const isEighth = Math.abs(ev.s % 0.5) < 0.1;

        // Logic 1: Low Tension (< 0.3) -> Restrict to Kick/Snare skeleton (Downbeats + Backbeats)
        if (tension < 0.3) {
            if (isDownbeat || isBackbeat) return true;
            return false;
        }

        // Logic 2: Medium Tension (< 0.5) -> Cap at 8th notes (No 16ths)
        if (tension < 0.5) {
            if (isEighth) return true;
            return false; // Drop 16ths
        }

        // Logic 3: High Tension (> 0.8) -> Allow everything (Fills, 16ths)
        // Default allow if tension >= 0.5
        return true;
    });
};

/**
 * INTELLIGENT TIMPANI FILTER (Defect 1 Fix)
 * Filters a raw stream of percussion events to ensure Timpani plays musically.
 * @param {Array} events - Raw 'perc_low' events (usually Kick drum pattern).
 * @param {Object} context - { tension, sectionType, bassEvents }
 */
export const filterTimpaniPart = (events, context) => {
    // Reuse the "Percussion Gate" logic for Timpani, but stricter
    // Timpani should be even more sparse than Drum Kit
    const tension = context.tension || 0.5;

    // First pass: Apply standard gate
    let filtered = filterPercussionByTension(events, tension);

    // Second pass: Timpani Specifics (No rapid fire unless roll)
    if (tension < 0.8) {
        let lastTime = -1;
        filtered = filtered.filter(ev => {
            if (ev.articulation === 'roll') return true;
            // Enforce Quarter Note gap minimum for single hits unless High Tension
            if (lastTime !== -1 && (ev.s - lastTime) < 0.9) return false;
            lastTime = ev.s;
            return true;
        });
    }

    return filtered;
};
